import { BaseCrud } from '@/shared/models/cruds.models'
import { Component, Ref } from 'vue-property-decorator'
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader.vue'
import VtGrid from '@/components/VtGrid/VtGrid.vue'
import { GetContextMenuItemsParams, MenuItemDef } from '@ag-grid-enterprise/all-modules'
import i18n from '@/lang/i18n'
import BaseCrudServerSideComponent from '@/shared/resources/base-crud-server-side.component'
import { initialTargetResponseTimesTypesGridState, TargetResponseTimesTypesGridState } from '@/modules/configurationModule/detail/local-samples/target-response-times/components/target-response-times-grid/target-response-times-grid.model'
import { targetResponseTimesConfigurationServices } from '@/modules/configurationModule/detail/local-samples/target-response-times/services/target-response-types.service'
import { ConfigurationTargetResponseTimeConfigurationCreateRequest, ConfigurationTargetResponseTimeConfigurationPatchInBatchRequest, ConfigurationTargetResponseTimeConfigurationResponse } from '@/modules/configurationModule/detail/local-samples/target-response-times/services/target-response-types.dto'
import TargetResponseTimesModal from '@/modules/configurationModule/detail/local-samples/target-response-times/components/target-response-times-modal/target-response-times-modal.vue'
import { EntityIdentifiersRequest } from '@/shared/models/http/http.model'

@Component({
    components: {
        ConfigurationSectionHeader,
        VtGrid,
        TargetResponseTimesModal
    }
})

export default class TargetResponseTimesGrid extends BaseCrudServerSideComponent<ConfigurationTargetResponseTimeConfigurationResponse> implements BaseCrud {
    @Ref() readonly localTargetResponseTimeModalWrapper!: any

    state: TargetResponseTimesTypesGridState
    constructor() {
        super()
        this.state = initialTargetResponseTimesTypesGridState()
    }

    get canDelete(): boolean {
        return !!this.state.selectedRows.length
    }

    get canUpdate(): boolean {
        const numberRowSelected: number = this.state.selectedRows.length
        return numberRowSelected === 1
    }

    getContextMenuItems(params:GetContextMenuItemsParams): MenuItemDef[] {
        return [
            {
                name: this.$t('configuration.actions.update').toString(),
                cssClasses: this.state.selectedRows.length > 1 ? ['not-show'] : [''],
                action: () => this.onUpdate(params?.node?.data)
            },
            {
                name: this.$t('configuration.actions.delete').toString(),
                cssClasses: this.state.selectedRows.length < 1 ? ['not-show'] : [''],
                action: () => this.onDelete()
            }
        ]
    }

    async createOperation(row: ConfigurationTargetResponseTimeConfigurationCreateRequest): Promise<void> {
        this.$_updateLoading(true)
        const [err, response] = await this.$_toCall(targetResponseTimesConfigurationServices.createTargetResponseTimesLaboratory(this.state.labId, row), true)
        this.$_updateLoading(false)
        if (err) {
            return
        } else if (response?.length) {
            this.$_handleErrors(response)
            return
        }
        this.localTargetResponseTimeModalWrapper.$slots.default[0].componentInstance.updateRow(response)
        this.state = {
            ...this.state,
            actionTitle: i18n.t('configuration.actions.update').toString()
        }
        this.$_showToastSuccess(this.$t('configuration.toastMessages.created')?.toString())
        this.reloadData()
    }

    async patchOperation(rows: ConfigurationTargetResponseTimeConfigurationPatchInBatchRequest[]): Promise<void> {
        this.$_updateLoading(true)
        const [err, response] = await this.$_toCall(targetResponseTimesConfigurationServices.patchTargetResponseTimesLaboratory(this.state.labId, rows), true)
        this.$_updateLoading(false)
        if (err) {
            return
        } else if (response?.length) {
            this.$_handleErrors(response)
            return
        }
        this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', 1))
        this.reloadData()
    }

    async deleteOperation(rows: ConfigurationTargetResponseTimeConfigurationResponse[]): Promise<void> {
        if (rows.length) {
            const selectedRowsSize:number = this.state?.selectedRows?.length
            const labelConfirmation:string = this.$t('configuration.grid.deleteConfirmation')?.toString()
            const labelGrid: string = this.$tc('configuration.grid.element', selectedRowsSize)
            const labelDeleteQuestion:string = this.$t('configuration.grid.deleteQuestion')?.toString()
            const labelDeleteAction:string = this.$t('configuration.actions.delete')?.toString()
            const promise = await this.$_showConfirmationModal(
                `${labelConfirmation} ${selectedRowsSize} ${labelGrid}, <br/> ${labelDeleteQuestion}`,
                labelDeleteAction
            )
            if (promise) {
                this._deleteBatchOperation(rows)
            }
        }
    }

    onAdd(): void {
        this.showModal(this.$t('configuration.actions.add').toString(), null, this.localTargetResponseTimeModalWrapper)
    }

    onUpdate(targetResponseTime: ConfigurationTargetResponseTimeConfigurationResponse = this.state.gridApi?.getSelectedRows()[0]): void {
        if (targetResponseTime) {
            this.showModal(this.$t('configuration.actions.update').toString(), targetResponseTime, this.localTargetResponseTimeModalWrapper)
        }
    }

    onDelete(): void {
        if (this.state.selectedRows.length) {
            this.deleteOperation(this.state.selectedRows)
        }
    }

    private async _deleteBatchOperation(rows: ConfigurationTargetResponseTimeConfigurationResponse[]): Promise<void> {
        const rowsToDelete: EntityIdentifiersRequest = {
            entityIdentifiers: rows.map((template: ConfigurationTargetResponseTimeConfigurationResponse) => Number(template?.id))
        }
        this.$_updateLoading(true)
        const [err, response] = await this.$_toCall(targetResponseTimesConfigurationServices.deleteTargetResponseTimesLaboratory(this.state.labId, rowsToDelete), true)
        this.$_updateLoading(false)
        if (err) {
            return
        } else if (response.length) {
            this.$_handleErrors(response)
            return
        }
        if (rows.length > 1) {
            this.$_showToastSuccess(
                this.$tc('configuration.toastMessages.deleted', 2, { elements: rows?.length })
            )
        } else {
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.deleted', 1))
        }
        this.reloadData()
    }
}
